import {Fragment, useCallback, useMemo, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {diApiEu} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import {
  createHistoryFilterSearch,
  createHistoryFilterSearchParams,
  getHistoryFilterFormState,
  HISTORY_FILTER_PERIOD,
  type HistoryFilterFormState,
  type HistoryFilterPeriod,
  type HistoryFilterQueryState,
  parseHistoryFilterSearchParams,
} from '@di-shared/features/history/filter/historyFilterQuery';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {ClickAwayListener} from '@mui/base';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import useIntersectionObserver from '@shared/useHandleObserver';
import {useInfiniteQuery} from '@tanstack/react-query';

interface FilteredDateHistoryResultEuProps {
  vin: string;
}

interface ResultModeContentProps {
  vin: string;
  filterState: HistoryFilterQueryState;
  resultPage: number;
  onOpenFilter(): void;
  onReplaceFilterState(partialState: Partial<HistoryFilterQueryState>): void;
  onReplaceResultPage(page: number): void;
}

const FILTERED_DATE_HISTORY_PAGE_SIZE = 3;
const FILTERED_DATE_HISTORY_INITIAL_PAGE = 1;

function parseResultPage(searchParams: URLSearchParams) {
  const page = Number(searchParams.get('page'));

  return Number.isFinite(page) && page >= FILTERED_DATE_HISTORY_INITIAL_PAGE ? page : FILTERED_DATE_HISTORY_INITIAL_PAGE;
}

function createResultSearchParams(filterState: HistoryFilterQueryState, page: number) {
  const params = createHistoryFilterSearchParams(filterState);

  if (page > FILTERED_DATE_HISTORY_INITIAL_PAGE) {
    params.set('page', String(page));
  }

  return params;
}

function getPeriodLabel(
  period: HistoryFilterPeriod,
  form: HistoryFilterFormState,
  t: ReturnType<typeof useInferableTranslation>['t'],
  CldrDatetimeFormatter: ReturnType<typeof useCldrDatetimeFormatter>
) {
  switch (period) {
    case HISTORY_FILTER_PERIOD.CURRENT_MONTH:
    case HISTORY_FILTER_PERIOD.PREVIOUS_1_MONTH:
    case HISTORY_FILTER_PERIOD.PREVIOUS_2_MONTH:
      return form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'MMM') : '';
    case HISTORY_FILTER_PERIOD.RECENT_7_DAYS:
      return `7 ${t('ctn_mytrips_download_day_02')}`;
    case HISTORY_FILTER_PERIOD.RECENT_30_DAYS:
      return `30 ${t('ctn_mytrips_download_day_02')}`;
    case HISTORY_FILTER_PERIOD.CUSTOM_DAYS:
      return t('ctn_mytrips_download_day_03');
  }
}

export function ResultModeFallback({onBack}: {onBack(): void}) {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton onClick={onBack} />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">All Trips</span>
        </h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <div className="page_loading_box">
          <div className="loading_lottie">
            <LottieSelector
              id="img_json_loading_black_60f"
              kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              loop={true}
              autoplay={true}
              style={{width: 24, height: 24}}
            />
          </div>
          <span className="loading_text">{t('msg_mytrips_list_loading_eu')}</span>
        </div>
      </main>
    </div>
  );
}

function ResultModeErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <div className="trip_info">
      <div className="empty_area">
        <span className="ic ic_caution_32px"></span>
        <span
          className="body_large_r"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {`${t('msg_mytrips_list_fail_load_data_eu')}\n${t('msg_mytrips_list_try_again_eu')}`}
        </span>
      </div>
    </div>
  );
}

export default function FilteredDateHistoryResultEu({vin}: FilteredDateHistoryResultEuProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterState = useMemo(() => parseHistoryFilterSearchParams(searchParams), [searchParams]);
  const resultPage = useMemo(() => parseResultPage(searchParams), [searchParams]);

  function replaceFilterState(partialState: Partial<HistoryFilterQueryState>) {
    const nextState = {
      ...filterState,
      ...partialState,
    };

    setSearchParams(createResultSearchParams(nextState, FILTERED_DATE_HISTORY_INITIAL_PAGE), {replace: true});
  }

  function replaceResultPage(page: number) {
    setSearchParams(createResultSearchParams(filterState, page), {replace: true});
  }

  function navigateToFilter() {
    navigate(
      {
        pathname: ROUTE_PATHS.HISTORY_FILTERED_FILTER.getFullPath(vin),
        search: createHistoryFilterSearch(filterState),
      },
      {replace: true}
    );
  }

  return (
    <ErrorBoundary fallback={<ResultModeErrorFallback />}>
      <InnerResultMode
        vin={vin}
        filterState={filterState}
        resultPage={resultPage}
        onOpenFilter={navigateToFilter}
        onReplaceFilterState={replaceFilterState}
        onReplaceResultPage={replaceResultPage}
      />
    </ErrorBoundary>
  );
}

function InnerResultMode({vin, filterState, resultPage, onOpenFilter, onReplaceFilterState, onReplaceResultPage}: ResultModeContentProps) {
  const navigate = useNavigate();
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const filterForm = useMemo(() => getHistoryFilterFormState(filterState), [filterState]);
  const selectedPeriodLabel = getPeriodLabel(filterForm.period, filterForm, t, CldrDatetimeFormatter);
  const {startDate, endDate, tagNames} = filterForm;

  if (!startDate || !endDate || startDate > endDate) {
    return <ResultModeFallback onBack={backOrClose} />;
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useInfiniteQuery({
    queryKey: [
      getQueryKey(diApiEu.drivingHistory.retrieveFilteredTripHistory),
      vin,
      getYYYYMMddFormat(startDate),
      getYYYYMMddFormat(endDate),
      tagNames,
    ],
    queryFn: ({pageParam = FILTERED_DATE_HISTORY_INITIAL_PAGE}) =>
      diApiEu.drivingHistory.retrieveFilteredTripHistory({
        vin,
        startDate: getYYYYMMddFormat(startDate),
        endDate: getYYYYMMddFormat(endDate),
        tagNames,
        displayOffsetDateTime: true,
        page: pageParam,
        size: FILTERED_DATE_HISTORY_PAGE_SIZE,
      }),
    initialPageParam: FILTERED_DATE_HISTORY_INITIAL_PAGE,
    getNextPageParam: (lastPage) => {
      const pageInfo = lastPage.data?.pageInfo;

      return pageInfo && pageInfo.currentPage < pageInfo.totalPages ? pageInfo.currentPage + 1 : undefined;
    },
  });

  const pages = data?.pages || [];
  const currentLoadedPage = pages.length;

  const firstPage = pages[0]?.data;
  const tripItems = pages.flatMap((page) => {
    return (page.data?.content ?? []).map((trip) => {
      const drvTime = trip.drvTime ?? 0;

      return {
        ...trip,
        drvTime,
        intervalIgnitionHours: Math.floor(drvTime / 60),
        intervalIgnitionMinutes: drvTime % 60,
        isDrivingLessThanOneMinute: drvTime === 0,
      };
    });
  });
  const fallbackSummaryData = tripItems.reduce(
    (summary, trip) => ({
      totalDistance: summary.totalDistance + (trip.drvDist ?? 0),
      totalDrivingSeconds: summary.totalDrivingSeconds + trip.drvTime * 60,
    }),
    {totalDistance: 0, totalDrivingSeconds: 0}
  );
  const totalDistance = firstPage?.totalDistance ?? fallbackSummaryData.totalDistance;
  const totalDrivingSeconds = firstPage?.totalRunTime ?? fallbackSummaryData.totalDrivingSeconds;
  const totalDrivingHours = Math.floor(totalDrivingSeconds / 3600);
  const totalDrivingMinutes = Math.floor((totalDrivingSeconds % 3600) / 60);
  const isTotalDrivingLessThanOneMinute = totalDrivingSeconds < 60;
  const isLoading = isHistoryLoading;
  const isError = isHistoryError;
  const isEmpty = !isLoading && !isError && firstPage?.pageInfo?.totalElements === 0;
  const shouldShowSummaryTimePlaceholder = isLoading || isError || isEmpty;

  const handleObserver = useCallback(
    async (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting || isFetchingNextPage || !hasNextPage) {
        return;
      }

      const nextPage = currentLoadedPage + 1;
      await fetchNextPage();
      onReplaceResultPage(nextPage);
    },
    [currentLoadedPage, fetchNextPage, hasNextPage, isFetchingNextPage, onReplaceResultPage]
  );

  const {observerRef} = useIntersectionObserver(handleObserver, {threshold: 0});

  return (
    <div className="wrap">
      <Header
        left={<HeaderBackButton />}
        right={
          <ClickAwayListener onClickAway={() => setIsMoreMenuOpen(false)}>
            <div className="btn_more_wrap">
              <button
                type="button"
                className="btn btn_ic"
                aria-label="More"
                aria-expanded={isMoreMenuOpen}
                aria-haspopup="menu"
                onClick={() => setIsMoreMenuOpen((prev) => !prev)}
              >
                <span
                  className="ic ic_menu_24px"
                  aria-hidden="true"
                ></span>
              </button>
              {isMoreMenuOpen && (
                <ul
                  className="more_menu"
                  role="menu"
                >
                  <li
                    role="none"
                    className="more_menu_item"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        navigate({pathname: ROUTE_PATHS.HISTORY_DOWNLOAD.getFullPath(vin), search: createHistoryFilterSearch(filterState)});
                      }}
                    >
                      {t('ctn_mytrips_dropdown_download_eu')}
                    </button>
                  </li>
                  <li
                    role="none"
                    className="more_menu_item"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
                      }}
                    >
                      {t('ctn_mytrips_dropdown_manage_tags_eu')}
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </ClickAwayListener>
        }
      >
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">All Trips</span>
        </h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_filter_complete">
          <div className="filter_setting">
            <div className="chips">
              <button
                type="button"
                className="chip chip_filter_line"
                onClick={onOpenFilter}
              >
                <span className="ic ic_filter_20px"></span>
                <span className="body_small_r" />
              </button>
              <button
                type="button"
                className="chip"
                onClick={onOpenFilter}
              >
                <span className="body_small_r">{selectedPeriodLabel}</span>
                <span className="ic ic_arrow_down_16px"></span>
              </button>
              {filterState.tagNames.map((tagName) => (
                <button
                  key={tagName}
                  type="button"
                  className="chip"
                  onClick={() => onReplaceFilterState({tagNames: filterState.tagNames.filter((name) => name !== tagName)})}
                >
                  <span className="body_small_r">{tagName}</span>
                  <span className="ic ic_close_12px"></span>
                </button>
              ))}
            </div>
          </div>
          <div className="driving_summary">
            <div className="driving_date">
              <span className="date_text">{`${CldrDatetimeFormatter.format(startDate, 'yMMMd')} - ${CldrDatetimeFormatter.format(endDate, 'yMMMd')}`}</span>
            </div>
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_time')}</dt>
                <dd className="driving_summary_info">
                  {shouldShowSummaryTimePlaceholder && (
                    <>
                      <span className="driving_summary_value">{ERROR.DASH}</span>
                      <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
                      <span className="driving_summary_value">{ERROR.DASH}</span>
                      <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
                    </>
                  )}
                  {!shouldShowSummaryTimePlaceholder && !!totalDrivingHours && (
                    <>
                      <span className="driving_summary_value">{totalDrivingHours}</span>
                      <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
                    </>
                  )}
                  {!shouldShowSummaryTimePlaceholder && !!totalDrivingMinutes && (
                    <>
                      <span className="driving_summary_value">{totalDrivingMinutes}</span>
                      <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
                    </>
                  )}
                  {!shouldShowSummaryTimePlaceholder && isTotalDrivingLessThanOneMinute && (
                    <span className="driving_summary_value">{t('imsi_less_minute')}</span>
                  )}
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_distance')}</dt>
                <dd className="driving_summary_info">
                  {isDistanceLessThan(totalDistance) ? (
                    <span className="driving_summary_value">
                      {t('imsi_less_distance_$unit', {
                        unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                      })}
                    </span>
                  ) : (
                    <>
                      <span className="driving_summary_value">{formatNumberWithLimitAndCommas(convertDistanceValue(totalDistance))}</span>
                      <span className="driving_summary_unit">{convertDistanceSymbol(totalDistance)}</span>
                    </>
                  )}
                </dd>
              </div>
            </dl>
          </div>
          {isLoading && (
            <div className="page_loading_box">
              <div className="loading_lottie">
                <LottieSelector
                  id="img_json_loading_black_60f"
                  kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  loop={true}
                  autoplay={true}
                  style={{width: 24, height: 24}}
                />
              </div>
              <span className="loading_text">{t('msg_mytrips_list_loading_eu')}</span>
            </div>
          )}
          {isError && <ResultModeErrorFallback />}
          {isEmpty && (
            <div className="trip_info">
              <div className="empty_area">
                <span className="ic ic_caution_32px"></span>
                <span className="body_large_r">{t('msg_mytrips_list_no_trips_eu')}</span>
              </div>
            </div>
          )}
          {tripItems.map((trip) => {
            const ignitionOnDate = parseDateWithFormat(trip.ignOnTime);
            const ignitionOffDate = parseDateWithFormat(trip.ignOffTime);

            return (
              <Fragment key={trip.id}>
                <div className="trip_info type_badge">
                  <div className="trip_info_head">
                    <span className="trip_info_item">
                      {ignitionOnDate && ignitionOffDate
                        ? DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')
                        : ERROR.DASH}
                    </span>
                    {trip.tags
                      ?.filter(({tagType}) => tagType === 'BUSINESS')
                      .map(({tagName}) => (
                        <span
                          key={tagName}
                          className="badge badge_basic_container"
                        >
                          {tagName}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="statistics_record_area">
                  <ul className="statistics_record_list">
                    <li className="statistics_record_item">
                      <span className="statistics_record_key">
                        <span className="ic ic_time_16px"></span>
                        {t('ctn_mytrips_time')}
                      </span>
                      <span className="statistics_record_val">
                        {!!trip.intervalIgnitionHours && (
                          <>
                            <span className="statistics_record_number">{trip.intervalIgnitionHours}</span>
                            <span className="statistics_record_unit">{t('ctn_mytrips_time_h_01')}</span>
                          </>
                        )}
                        {!!trip.intervalIgnitionMinutes && (
                          <>
                            <span className="statistics_record_number">{trip.intervalIgnitionMinutes}</span>
                            <span className="statistics_record_unit">{t('ctn_mytrips_time_min_01')}</span>
                          </>
                        )}
                        {!trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes && (
                          <span className="statistics_record_number">{t('imsi_less_minute')}</span>
                        )}
                      </span>
                    </li>
                    <li className="statistics_record_item">
                      <span className="statistics_record_key">
                        <span className="ic ic_drive_distance_16px"></span>
                        {t('ctn_mytrips_distance')}
                      </span>
                      <span className="statistics_record_val">
                        {isDistanceLessThan(trip.drvDist ?? 0) ? (
                          <span className="statistics_record_unit">
                            {t('imsi_less_distance_$unit', {
                              unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                            })}
                          </span>
                        ) : (
                          <>
                            <span className="statistics_record_number">
                              {formatNumberWithCommas(convertDistanceValue(trip.drvDist ?? 0))}
                            </span>
                            <span className="statistics_record_unit">{convertDistanceSymbol(trip.drvDist ?? 0)}</span>
                          </>
                        )}
                      </span>
                    </li>
                    <li className="statistics_record_item">
                      <span className="statistics_record_key">
                        <span className="ic ic_avgspeed_16px"></span>
                        {t('ctn_mytrips_avg_speed')}
                      </span>
                      <span className="statistics_record_val">
                        <span className="statistics_record_number">{formatNumberWithCommas(convertSpeedValue(trip.avgSpeed ?? 0))}</span>
                        <span className="statistics_record_unit">{convertSpeedSymbol(trip.avgSpeed ?? 0)}</span>
                      </span>
                    </li>
                  </ul>
                  <button
                    type="button"
                    className="btn_learn_more"
                    onClick={() => navigate(ROUTE_PATHS.HISTORY_$TRIP_ID.getFullPath(vin, trip.id.toString()))}
                  >
                    {t('msg_mytrips_see_more_eu')}
                    <span className="ic ic_arrow_forward_12px"></span>
                  </button>
                </div>
              </Fragment>
            );
          })}
          {(isFetchingNextPage || hasNextPage) && (
            <div
              className="page_loading_box"
              ref={observerRef}
            >
              <div className="loading_lottie">
                <LottieSelector
                  id="img_json_loading_black_60f"
                  kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                  loop={true}
                  autoplay={true}
                  style={{width: 24, height: 24}}
                />
              </div>
              <span className="loading_text">{t('msg_mytrips_list_loading_eu')}</span>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
